import moment from "moment"
import { isStaffInvitationReady } from "../../utils/prospect/backgroundCheckCta"

// CCMS background check statuses (Membership::Prospect::GetBackgroundCheck)
// that mean a screening is underway but has no result yet.
const BACKGROUND_CHECK_PENDING_STATUSES = new Set([
  "IN_PROGRESS",
  "IN_REVIEW",
  "DOCUMENTATION_NEEDED",
])

export const BACKGROUND_CHECK_VALID_FOR_YEARS = 1

export const BACKGROUND_CHECK_STEP_STATE = {
  APPROVED: "approved",
  IN_PROGRESS: "in_progress",
  DENIED: "denied",
  EXPIRED: "expired",
  NOT_STARTED: "not_started",
}

// Reinstatement needs a check screened less than a year ago. CCMS only flips
// APPROVED to EXPIRED the day after the anniversary and never expires an
// approval that has no screening date, so the clock is applied here.
export const backgroundCheckExpiresAt = screenedAt =>
  screenedAt
    ? moment(screenedAt).add(BACKGROUND_CHECK_VALID_FOR_YEARS, "year")
    : null

// The latest HireRight order stays on the prospect for a year after it
// completes, so a Completed order only counts while it is newer than the CCMS
// screening (staff have not recorded its result yet).
const isOrderOpen = (order, screenedAt) => {
  if (!order || order.status === "Canceled") return false
  if (order.status === "Completed") {
    return !screenedAt || moment(order.createdAt).isAfter(screenedAt)
  }
  return Boolean(
    order.awaitingModeration || order.paidAt || isStaffInvitationReady(order),
  )
}

export const resolveBackgroundCheckStep = (
  { backgroundCheck, order } = {},
  now = moment(),
) => {
  const status = backgroundCheck?.status
  const expiresAt = backgroundCheckExpiresAt(backgroundCheck?.screenedAt)

  if (status === "APPROVED" && expiresAt?.isAfter(now)) {
    return {
      state: BACKGROUND_CHECK_STEP_STATE.APPROVED,
      complete: true,
      expiresAt: expiresAt.toISOString(),
    }
  }

  if (status === "DENIED") {
    return { state: BACKGROUND_CHECK_STEP_STATE.DENIED, complete: false }
  }

  const orderOpen = isOrderOpen(order, backgroundCheck?.screenedAt)
  if (orderOpen || BACKGROUND_CHECK_PENDING_STATUSES.has(status)) {
    return {
      state: BACKGROUND_CHECK_STEP_STATE.IN_PROGRESS,
      complete: false,
      canContinue: orderOpen && !order.awaitingModeration,
    }
  }

  if (status === "APPROVED" || status === "EXPIRED") {
    return {
      state: BACKGROUND_CHECK_STEP_STATE.EXPIRED,
      complete: false,
      expiredAt: expiresAt?.toISOString() || backgroundCheck.expiredAt || null,
    }
  }

  return { state: BACKGROUND_CHECK_STEP_STATE.NOT_STARTED, complete: false }
}
